import React, { useEffect, useMemo, useRef, useState } from "react";
import { fmtTime } from "@/lib/fmt";

// Real solar day for the user's own location & clock: the sun rises on screen at the local sunrise, tracks across
// the sky and sets at the local sunset; night shows the moon and stars. Location: browser geolocation (cached), else a
// timezone-based approximation.
const STARS = Array.from({ length: 70 }, (_, i) => ({
  x: (i * 37 + 13) % 100, y: (i * 53 + 7) % 65, s: 1 + (i % 3), d: (i % 5) * 0.7,
}));

const TZ_FALLBACK = {
  "America/New_York": [40.7, -74.0], "America/Detroit": [42.3, -83.0], "America/Chicago": [41.9, -87.6], "America/Denver": [39.7, -105.0],
  "America/Phoenix": [33.4, -112.1], "America/Los_Angeles": [34.1, -118.2], "America/Anchorage": [61.2, -149.9], "Pacific/Honolulu": [21.3, -157.9],
  "America/Boise": [43.6, -116.2], "America/Indiana/Indianapolis": [39.8, -86.2], "America/Kentucky/Louisville": [38.3, -85.8],
};
const LOC_KEY = "mfg_sky_loc";
const WX_KEY = "mfg_sky_wx";
const WX_TTL = 15 * 60000;

// WMO weather codes → what the sky shows
function weatherKind(code, cloud) {
  if (code == null) return { kind: "clear", clouds: 0 };
  if (code >= 95) return { kind: "storm", clouds: 5 };
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return { kind: "snow", clouds: 4 };
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return { kind: "rain", clouds: code >= 63 || code >= 81 ? 5 : 3 };
  if (code === 45 || code === 48) return { kind: "fog", clouds: 4 };
  const c = cloud == null ? (code === 3 ? 80 : code === 2 ? 50 : code === 1 ? 20 : 0) : cloud;
  return { kind: "clear", clouds: c >= 85 ? 5 : c >= 60 ? 4 : c >= 35 ? 2 : c >= 15 ? 1 : 0 };
}

const CLOUDS = [
  { x: 8, y: 14, w: 260, h: 70, d: 0, s: 90 }, { x: 38, y: 8, w: 340, h: 90, d: -25, s: 120 }, { x: 66, y: 18, w: 220, h: 60, d: -50, s: 75 },
  { x: 22, y: 30, w: 300, h: 80, d: -70, s: 105 }, { x: 78, y: 32, w: 280, h: 74, d: -12, s: 95 },
];
const DROPS = Array.from({ length: 90 }, (_, i) => ({ x: (i * 41 + 5) % 100, d: (i % 10) * 0.13, l: 14 + (i % 4) * 5, sp: 0.9 + (i % 3) * 0.15 }));
const FLAKES = Array.from({ length: 60 }, (_, i) => ({ x: (i * 29 + 3) % 100, d: (i % 8) * 0.7, s: 2 + (i % 3), sp: 7 + (i % 4) * 1.5 }));

function Weather({ wx, night }) {
  const { kind, clouds } = wx;
  const tint = night ? "rgba(190,200,225," : "rgba(255,255,255,";
  return (
    <>
      <style>{`@keyframes mfgCloud{from{transform:translateX(-12vw)}to{transform:translateX(112vw)}}@keyframes mfgRain{from{transform:translateY(-10vh)}to{transform:translateY(110vh)}}@keyframes mfgSnow{from{transform:translateY(-6vh) translateX(0)}50%{transform:translateY(50vh) translateX(18px)}to{transform:translateY(110vh) translateX(-6px)}}@keyframes mfgFlash{0%,92%,100%{opacity:0}93%{opacity:.55}95%{opacity:0}97%{opacity:.35}}`}</style>
      {clouds >= 4 && <div className="absolute inset-0" style={{ background: night ? "rgba(20,26,44,0.45)" : kind === "storm" ? "rgba(40,48,66,0.55)" : "rgba(120,132,150,0.35)" }} />}
      {CLOUDS.slice(0, clouds).map((c, i) => {
        const base = kind === "clear" ? (night ? "#c9d2e6" : "#ffffff") : kind === "storm" ? (night ? "#4a5468" : "#6b7688") : (night ? "#8e98ad" : "#b9c2d0");
        const shade = night ? "rgba(60,70,95,0.55)" : "rgba(90,100,120,0.35)";
        return (
          <div key={i} className="absolute" data-testid={`sky-cloud-${i}`}
            style={{ left: 0, top: `${c.y}%`, width: c.w, height: c.h, opacity: kind === "clear" ? 0.92 : 0.97, filter: "blur(0.6px)",
              animation: `mfgCloud ${c.s}s linear infinite`, animationDelay: `${c.d}s` }}>
            {[[0, 42, 46, 58], [20, 10, 56, 90], [48, 4, 60, 96], [74, 30, 46, 64], [36, 46, 70, 54]].map(([l, t, w, hh], k) => (
              <span key={k} className="absolute rounded-full" style={{ left: `${l}%`, top: `${t}%`, width: `${w}%`, height: `${hh}%`,
                background: `radial-gradient(circle at 40% 35%, ${base} 0%, ${base} 55%, ${shade} 100%)`, boxShadow: `inset -6px -8px 14px ${shade}` }} />
            ))}
          </div>
        );
      })}
      {(kind === "rain" || kind === "storm") && DROPS.map((r, i) => (
        <span key={i} className="absolute" style={{ left: `${r.x}%`, top: 0, width: 1.5, height: r.l + 6, borderRadius: 2, background: `linear-gradient(180deg, transparent, ${night ? "rgba(190,210,245,0.75)" : "rgba(235,243,255,0.9)"})`,
          animation: `mfgRain ${r.sp}s linear infinite`, animationDelay: `${r.d}s`, transform: "translateY(-10vh) rotate(8deg)" }} />
      ))}
      {kind === "snow" && FLAKES.map((f, i) => (
        <span key={i} className="absolute rounded-full bg-white" style={{ left: `${f.x}%`, top: 0, width: f.s, height: f.s, opacity: 0.9, animation: `mfgSnow ${f.sp}s linear infinite`, animationDelay: `${f.d}s` }} />
      ))}
      {kind === "fog" && <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${tint}0.15) 0%, ${tint}0.45) 100%)` }} />}
      {kind === "storm" && <div className="absolute inset-0 bg-white" style={{ animation: "mfgFlash 9s linear infinite" }} />}
    </>
  );
}

function fallbackLocation() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return TZ_FALLBACK[tz] || [39.0, -98.0]; // geographic center of the lower 48
}

// NOAA sunrise/sunset → local hours (fractional) for the given date; handles polar edge cases by clamping.
function solarTimes(date, lat, lng) {
  const rad = Math.PI / 180;
  const start = new Date(date.getFullYear(), 0, 0);
  const doy = Math.floor((date - start) / 86400000);
  const lngHour = lng / 15;
  const calc = (rising) => {
    const t = doy + ((rising ? 6 : 18) - lngHour) / 24;
    const M = 0.9856 * t - 3.289;
    let L = M + 1.916 * Math.sin(M * rad) + 0.02 * Math.sin(2 * M * rad) + 282.634;
    L = ((L % 360) + 360) % 360;
    let RA = Math.atan(0.91764 * Math.tan(L * rad)) / rad;
    RA = ((RA % 360) + 360) % 360;
    RA += Math.floor(L / 90) * 90 - Math.floor(RA / 90) * 90;
    RA /= 15;
    const sinDec = 0.39782 * Math.sin(L * rad);
    const cosDec = Math.cos(Math.asin(sinDec));
    const cosH = (Math.cos(90.833 * rad) - sinDec * Math.sin(lat * rad)) / (cosDec * Math.cos(lat * rad));
    if (cosH > 1) return rising ? 12 : 12; // sun never rises
    if (cosH < -1) return rising ? 0 : 24; // sun never sets
    const H = (rising ? 360 - Math.acos(cosH) / rad : Math.acos(cosH) / rad) / 15;
    const T = H + RA - 0.06571 * t - 6.622;
    const UT = ((T - lngHour) % 24 + 24) % 24;
    return ((UT - date.getTimezoneOffset() / 60) % 24 + 24) % 24;
  };
  let sunrise = calc(true), sunset = calc(false);
  if (sunset <= sunrise) sunset += 24;
  return { sunrise, sunset };
}

const SKY = {
  dawn: "linear-gradient(180deg,#1b2552 0%,#6a4a9a 28%,#f08a5d 58%,#ffd79b 100%)",
  morning: "linear-gradient(180deg,#2a6fc0 0%,#63aeea 55%,#a9d3f4 100%)",
  day: "linear-gradient(180deg,#145fb8 0%,#3f9ae4 50%,#93c8f0 100%)",
  golden: "linear-gradient(180deg,#245a9c 0%,#7fa3d6 32%,#ffb073 72%,#ffe0ad 100%)",
  dusk: "linear-gradient(180deg,#0f1538 0%,#4c2a6e 38%,#f2744c 76%,#ffb98a 100%)",
  night: "linear-gradient(180deg,#02040c 0%,#07102a 50%,#10203f 100%)",
};
// Synodic lunar phase 0..1 (0 = new, 0.5 = full) from a known new moon (2000-01-06 18:14 UTC)
function moonPhase(d) {
  const syn = 29.530588853;
  const days = (d.getTime() - Date.UTC(2000, 0, 6, 18, 14)) / 86400000;
  return ((days / syn) % 1 + 1) % 1;
}
const PHASE_NAMES = ["New moon", "Waxing crescent", "First quarter", "Waxing gibbous", "Full moon", "Waning gibbous", "Last quarter", "Waning crescent"];

function Moon({ phase, size = 84 }) {
  // Terminator: shift a dark disc across the lit disc. illum = 0 (new) → 1 (full)
  const illum = (1 - Math.cos(phase * 2 * Math.PI)) / 2;
  const waxing = phase < 0.5;
  const shift = (1 - illum) * size * 1.15; // how far the shadow disc is offset
  return (
    <div className="relative rounded-full" data-testid="sky-moon" data-illumination={Math.round(illum * 100)}
      style={{ width: size, height: size, background: "radial-gradient(circle at 38% 36%, #fdfdfa 0%, #e6e9f0 48%, #b7c0d3 100%)",
        boxShadow: `0 0 ${40 + illum * 60}px ${10 + illum * 18}px rgba(215,225,255,${0.35 + illum * 0.3}), inset -6px -8px 18px rgba(120,130,160,0.45)` }}>
      {[[22, 28, 16], [56, 50, 11], [38, 66, 8], [66, 24, 7], [30, 50, 6]].map(([l, t, w], i) => (
        <span key={i} className="absolute rounded-full" style={{ left: `${l}%`, top: `${t}%`, width: w, height: w, background: "radial-gradient(circle at 40% 40%, rgba(150,160,185,0.55), rgba(120,130,160,0.75))", boxShadow: "inset 1px 1px 2px rgba(255,255,255,0.35)" }} />
      ))}
      <div className="absolute inset-0 rounded-full overflow-hidden">
        <div className="absolute rounded-full" style={{ width: size, height: size, top: 0, left: waxing ? -shift : shift, background: "rgba(8,12,28,0.93)", transition: "left 2s ease" }} />
      </div>
    </div>
  );
}

function Sun({ phase }) {
  const warm = phase === "dawn" || phase === "dusk";
  const golden = phase === "golden";
  const core = warm ? "#ffd9a8" : "#fffdf3";
  const mid = warm ? "#ff9f5a" : golden ? "#ffd27a" : "#ffe98a";
  const glow = warm ? "255,130,70" : golden ? "255,190,110" : "255,225,150";
  return (
    <div className="relative" data-testid="sky-sun" style={{ width: 150, height: 150 }}>
      <style>{`@keyframes mfgRays{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <div className="absolute inset-0 rounded-full" style={{ background: `conic-gradient(from 0deg, rgba(${glow},0) 0deg, rgba(${glow},0.35) 8deg, rgba(${glow},0) 16deg, rgba(${glow},0) 40deg, rgba(${glow},0.3) 48deg, rgba(${glow},0) 56deg, rgba(${glow},0) 90deg, rgba(${glow},0.35) 98deg, rgba(${glow},0) 106deg, rgba(${glow},0) 140deg, rgba(${glow},0.3) 148deg, rgba(${glow},0) 156deg, rgba(${glow},0) 200deg, rgba(${glow},0.35) 208deg, rgba(${glow},0) 216deg, rgba(${glow},0) 250deg, rgba(${glow},0.3) 258deg, rgba(${glow},0) 266deg, rgba(${glow},0) 300deg, rgba(${glow},0.35) 308deg, rgba(${glow},0) 316deg)`, filter: "blur(6px)", transform: "scale(1.9)", animation: "mfgRays 90s linear infinite", opacity: 0.9 }} />
      <div className="absolute rounded-full" style={{ inset: 22, background: `radial-gradient(circle, ${core} 0%, ${mid} 42%, rgba(${glow},0.9) 60%, rgba(${glow},0) 72%)`, boxShadow: `0 0 90px 36px rgba(${glow},0.75), 0 0 220px 110px rgba(${glow},0.35)` }} />
      <div className="absolute rounded-full" style={{ inset: 44, background: core, filter: "blur(1.5px)", boxShadow: `0 0 30px 10px ${core}` }} />
    </div>
  );
}

export default function SkyArch() {
  const [now, setNow] = useState(() => new Date());
  const [loc, setLoc] = useState(() => {
    try { const c = JSON.parse(localStorage.getItem(LOC_KEY) || "null"); if (c && c.lat != null) return c; } catch { /* ignore */ }
    return null;
  });
  const [locState, setLocState] = useState(loc ? "gps" : "idle");
  // Keep the sky inside the content area (to the right of the fixed sidebar) so the sun/moon are never hidden behind it
  const ref = useRef(null);
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const measure = () => { const pr = ref.current?.parentElement?.getBoundingClientRect(); setLeft(pr ? Math.max(0, Math.round(pr.left)) : 0); };
    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 800);
    return () => { window.removeEventListener("resize", measure); clearTimeout(t); };
  }, []);
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  const askLocation = () => {
    if (!navigator.geolocation) return setLocState("unsupported");
    setLocState("asking");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude, at: Date.now() };
        try { localStorage.setItem(LOC_KEY, JSON.stringify(c)); } catch { /* ignore */ }
        setLoc(c); setLocState("gps");
      },
      () => setLocState("denied"), { maximumAge: 6 * 3600000, timeout: 10000 },
    );
  };
  useEffect(() => { if (!loc) askLocation(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [lat, lng] = loc ? [loc.lat, loc.lng] : fallbackLocation();
  const [wx, setWx] = useState(() => {
    try { const c = JSON.parse(localStorage.getItem(WX_KEY) || "null"); if (c && Date.now() - c.at < WX_TTL) return c; } catch { /* ignore */ }
    return null;
  });
  useEffect(() => {
    let stop = false;
    const pull = async () => {
      try {
        const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat.toFixed(3)}&longitude=${lng.toFixed(3)}&current=weather_code,cloud_cover,precipitation,temperature_2m,wind_speed_10m&temperature_unit=fahrenheit&wind_speed_unit=mph`);
        const j = await r.json();
        const cur = j.current || {};
        const w = { at: Date.now(), code: cur.weather_code, cloud: cur.cloud_cover, temp: cur.temperature_2m, wind: cur.wind_speed_10m, lat, lng };
        try { localStorage.setItem(WX_KEY, JSON.stringify(w)); } catch { /* ignore */ }
        if (!stop) setWx(w);
      } catch { /* offline → keep last / clear sky */ }
    };
    if (!wx || Date.now() - wx.at > WX_TTL || Math.abs((wx.lat ?? lat) - lat) > 0.2) pull();
    const id = setInterval(pull, WX_TTL);
    return () => { stop = true; clearInterval(id); };
  }, [lat, lng]); // eslint-disable-line react-hooks/exhaustive-deps
  const weather = weatherKind(wx?.code, wx?.cloud);
  const { sunrise, sunset } = useMemo(() => solarTimes(now, lat, lng), [now.getDate(), now.getMonth(), lat, lng]); // eslint-disable-line react-hooks/exhaustive-deps
  const h = now.getHours() + now.getMinutes() / 60 + now.getSeconds() / 3600;
  const isDay = h >= sunrise && h < sunset;
  const dayLen = sunset - sunrise;
  const p = isDay ? (h - sunrise) / dayLen : ((h >= sunset ? h - sunset : h + 24 - sunset) / (24 - dayLen));
  const x = 6 + p * 88;
  const y = 82 - Math.sin(Math.PI * p) * 62;
  const sinceRise = h - sunrise, toSet = sunset - h;
  const phase = !isDay ? "night" : sinceRise < 0.75 ? "dawn" : sinceRise < 2.5 ? "morning" : toSet < 0.75 ? "dusk" : toSet < 2.5 ? "golden" : "day";
  const fmt = (v) => { const hh = Math.floor(v % 24), mm = Math.round((v % 1) * 60); const d = new Date(); d.setHours(hh, mm, 0, 0); return fmtTime(d); };
  const mp = moonPhase(now);
  const moonName = PHASE_NAMES[Math.round(mp * 8) % 8];
  const dim = weather.clouds >= 4 ? (weather.kind === "clear" ? 0.45 : 0.3) : weather.clouds >= 2 ? 0.8 : 1;
  const WX_LABEL = { clear: weather.clouds >= 4 ? "Overcast" : weather.clouds >= 2 ? "Partly cloudy" : isDay ? "Sunny" : "Clear", rain: "Rain", storm: "Thunderstorm", snow: "Snow", fog: "Fog" };
  const sunGlow = isDay ? `radial-gradient(circle at ${x}% ${y}%, rgba(255,${phase === "dawn" || phase === "dusk" ? "150,90" : "230,170"},${phase === "day" ? 0.22 : 0.4}) 0%, rgba(255,255,255,0) 38%)` : `radial-gradient(circle at ${x}% ${y}%, rgba(200,215,255,0.16) 0%, rgba(0,0,0,0) 30%)`;

  return (
    <div ref={ref} aria-hidden data-testid="sky-arch" data-sky-phase={phase} data-sunrise={fmt(sunrise)} data-sunset={fmt(sunset)} data-loc={loc ? "gps" : "tz"}
      data-weather={weather.kind} data-clouds={weather.clouds} data-wmo={wx?.code ?? ""} data-moon={moonName}
      className="pointer-events-none fixed inset-y-0 right-0 z-0 overflow-hidden"
      style={{ left, background: `${sunGlow}, ${SKY[phase]}`, transition: "background 20s linear, opacity 5s ease" }}>
      {phase === "night" && <div className="absolute inset-0" style={{ background: "linear-gradient(115deg, transparent 30%, rgba(170,185,230,0.10) 45%, rgba(200,210,245,0.16) 50%, rgba(170,185,230,0.10) 55%, transparent 70%)", filter: "blur(18px)" }} />}
      {phase === "night" && STARS.map((s, i) => (
        <span key={i} className="absolute rounded-full bg-white animate-pulse"
          style={{ left: `${s.x}%`, top: `${s.y}%`, width: s.s, height: s.s, animationDelay: `${s.d}s`, opacity: 0.95 * dim, boxShadow: s.s > 2 ? "0 0 8px 2px rgba(255,255,255,0.75)" : "0 0 3px 1px rgba(255,255,255,0.4)" }} />
      ))}
      {(phase === "dawn" || phase === "dusk") && STARS.slice(0, 18).map((s, i) => (
        <span key={`f${i}`} className="absolute rounded-full bg-white" style={{ left: `${s.x}%`, top: `${s.y * 0.5}%`, width: 1.5, height: 1.5, opacity: 0.45 }} />
      ))}
      <div className="absolute -translate-x-1/2 -translate-y-1/2"
        style={{ left: `${x}%`, top: `${y}%`, transition: "left 30s linear, top 30s linear, opacity 3s ease", opacity: dim }}>
        {isDay ? <Sun phase={phase} /> : <Moon phase={mp} />}
      </div>
      <Weather wx={weather} night={!isDay} />
      {/* ground vignette: keeps light HUD text readable over a bright sky without greying it out */}
      <div className="absolute bottom-0 inset-x-0 h-56"
        style={{ background: phase === "night" ? "linear-gradient(180deg,transparent,rgba(5,9,20,0.6))" : "linear-gradient(180deg,transparent,rgba(8,14,30,0.45))" }} />
      <div className="absolute bottom-3 right-3 pointer-events-auto flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold backdrop-blur-md"
        data-testid="sky-weather-chip" style={{ background: "rgba(10,14,28,0.55)", color: "#e8eefc", border: "1px solid rgba(255,255,255,0.15)" }}>
        <span>{WX_LABEL[weather.kind]}{wx?.temp != null ? ` · ${Math.round(wx.temp)}°F` : ""}{wx?.wind != null && wx.wind >= 15 ? ` · wind ${Math.round(wx.wind)} mph` : ""}</span>
        <span className="opacity-60">· {isDay ? `sets ${fmt(sunset)}` : `${moonName} · rises ${fmt(sunrise)}`}</span>
        {!loc && (
          <button type="button" onClick={askLocation} data-testid="sky-use-location" className="ml-1 rounded-full px-2 py-0.5 font-bold !min-h-0"
            style={{ background: "#0A84FF", color: "#fff" }}>{locState === "asking" ? "Locating…" : locState === "denied" ? "Location blocked — allow in browser" : "Use my location"}</button>
        )}
      </div>
    </div>
  );
}
